import { Component, Input, NgZone, ChangeDetectorRef, ViewEncapsulation, ElementRef } from '@angular/core';
import { TimeoutQueue } from '@common/services/timeout-queue';
import { AppService } from '@suite/app.service';
import { GlobalTablesService } from "@common/services/global-tables-service";
import { VisualStateService } from "@common/services/visual-state-service";
import { LanguageSettings } from "@common/services/language-settings";
import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeFlattener, MatTreeFlatDataSource } from '@angular/material/tree';
import { Subject } from 'rxjs';
import { EnpsControl } from '../enps-control/enps-control';


interface TreeNode {
    value: string,
    id: string,
    parentId?: string,
    children?: TreeNode[]
}

interface FlatTreeNode {
    value: string,
    id: string,
    parentId?: string,
    level: number,
    expandable: boolean,
    selected: boolean
}

@Component({
    selector: 'tree',
    templateUrl: './tree.html',
    styleUrls: ['./tree.scss'],
    encapsulation: ViewEncapsulation.None,
    host: {
        'class': 'st-tree'
    }
})

export class Tree extends EnpsControl<string> {

    private _transformer = (node: TreeNode, level: number) => {
        return {
            expandable: !!node.children && node.children.length > 0,
            value: node.value,
            id: node.id,
            parentId: node.parentId,
            level: level,
            selected: (node.id === this.value)
        };
    };

    treeControl = new FlatTreeControl<FlatTreeNode>(
        node => node.level,
        node => node.expandable,
    );

    treeFlattener = new MatTreeFlattener(
        this._transformer,
        node => node.level,
        node => node.expandable,
        node => node.children,
    );
    dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
    hasChild = (_: number, node: FlatTreeNode) => node.expandable;

    @Input() public itemList: TreeNode[] = [];
    
    private _panelOpen = false;
    get panelOpen(): boolean {
        return this._panelOpen;
    }

    protected _canOpen(): boolean {
        return !this._panelOpen && !this.disabled;
    }

    constructor(
        public appService: AppService,
        ngZone: NgZone,
        changeDetectorRef: ChangeDetectorRef,
        timeoutQueue: TimeoutQueue,
        globalTablesService: GlobalTablesService,
        public visualStateService: VisualStateService,
        public languagesettings: LanguageSettings,
        public _changeDetectorRef: ChangeDetectorRef,
        elmRef: ElementRef
    ) {
        super(appService, ngZone, changeDetectorRef, timeoutQueue, globalTablesService, elmRef);
    }

    ngAfterViewInit() {
        this.dataSource.data = this.itemList;
    }

    toggle(): void {
        if (this.panelOpen) {
            this.close();
            return;
        }
        this.open();
    }

    protected readonly _destroy = new Subject<void>();

    /** Opens the overlay panel. */
    open(): void {
        if (this._canOpen()) {
            this._calculateSelectedNode();
            this._panelOpen = true;
        }
    }

    /** Closes the overlay panel and focuses the host element. */
    close(): void {
        if (this._panelOpen) {
            this._panelOpen = false;

        }
    }

    get displayValue(): string {
        if(this.treeControl.dataNodes) {
            let nodeValue = this.treeControl.dataNodes.find(n => n.id === this.value);
            if(nodeValue && nodeValue.id == this.value) {
                return nodeValue.value;
            }
            return '';
        }
    }

    hasChildren(_: number, node) {
        return node.Children.length > 0;
    }

    private _calculateSelectedNode() {
        this.treeControl.dataNodes.forEach(n => n.selected = false);
        let node = this.treeControl.dataNodes.find(n => n.id === this.value);
        if (node) {
            node.selected = true;
        }
    }

    handleSelected(node: FlatTreeNode) {
        let prevVal = this.value;
        this.value = node.id;
        if (node.id !== prevVal) {
            this.changed.emit(this.value);
            this.close();
        }
    }
}
